import { Button, ButtonColors } from '@chiroup/components/Button';
import { OpenClosedStates } from '@chiroup/components/states';
import { ListClinic } from '@chiroup/core/types/Clinic.type';
import { ErrorResponse } from '@chiroup/core/types/ErrorResponse.type';
import dayjs from 'dayjs';
import qs from 'query-string';
import React, { use, useEffect, useMemo, useState } from 'react';
import { UseMutateFunction } from 'react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ToastContext, ToastTypes } from '../../../contexts/toast.context';
import clinicService from '../../../services/clinic.service';
import InfoItem from '../../common/info/InfoItem';
import Modal from '../../common/Modal';
import MoreButton from '../../common/MoreButton';
import SectionContainer from '../../layout/SectionContainer';
import SectionHeader from '../../layout/SectionHeader';
import ClinicDeleteModal from './ClinicDeleteModal';
import ClinicGeneralEditPanel from './ClinicGeneralEditPanel';

type Props = {
  clinic: Partial<ListClinic>;
  update: UseMutateFunction<Partial<ListClinic>, ErrorResponse, Partial<ListClinic>, unknown>;
  del: () => Promise<void>;
  isUpdating: boolean;
  user: any;
};

const ClinicGeneral: React.FC<Props> = ({ clinic, update, isUpdating, del, user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { createToast } = use(ToastContext);
  const [deleteModalState, setDeleteModalState] = useState<OpenClosedStates>(
    OpenClosedStates.Closed
  );
  const [editSlideOverState, setEditSlideOverState] = useState<OpenClosedStates>(
    OpenClosedStates.Closed
  );
  const [teardownModalOpen, setTeardownModalOpen] = useState(false);
  const [isTearingDown, setIsTearingDown] = useState(false);

  const hasTeardownAccess = useMemo(() => {
    const email = user?.signInDetails?.loginId?.toLowerCase() || user?.email?.toLowerCase() || '';
    const allowedEmails = ['jeremy@chiroup.com', 'kara@chiroup.com', 'k.tegtmeier@chiroup.com'];

    return allowedEmails.includes(email);
  }, [user]);

  const onTeardownMessaging = async () => {
    if (!clinic.ID) return;
    setIsTearingDown(true);

    try {
      await clinicService.teardownMessaging(Number(clinic.ID));
      setTeardownModalOpen(false);
      createToast({
        title: 'Success!',
        description: <>Successfully tore down messaging for this clinic.</>,
        type: ToastTypes.Success,
        duration: 5000,
      });
    } catch {
      createToast({
        title: 'Error!',
        description: <>Failed to teardown messaging.</>,
        type: ToastTypes.Fail,
        duration: 5000,
      });
    }

    setIsTearingDown(false);
  };

  useEffect(() => {
    const queryParams = qs.parse(location.search);
    const editOpen = queryParams.open?.includes('edit');
    setEditSlideOverState(editOpen ? OpenClosedStates.Open : OpenClosedStates.Closed);
  }, [location.search]);

  let clinicPlan;

  if (clinic.plan) {
    clinicPlan = clinic.plan?.slice(0, 1).toUpperCase() + clinic.plan?.slice(1);
  }

  const updateSlideOverState = () => {
    navigate(`/clinics/${clinic.ID}`);
  };

  const editClinic = () => {
    navigate(`/clinics/${clinic.ID}?open=edit`);
  };

  const onClickDelete = () => {
    setDeleteModalState(OpenClosedStates.Open);
  };

  return (
    <>
      <SectionContainer className="sm:shadow border border-gray-300 dark:border-dark-gray-800 mx-6">
        <SectionHeader
          title="General"
          subtitle="General information about the clinic."
          rightSide={
            <div className="flex flex-row items-center space-x-2.5">
              <MoreButton
                options={[
                  {
                    text: 'Edit',
                    icon: (
                      <svg
                        className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-500"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                    ),
                    onClick: editClinic,
                    className: 'block sm:hidden',
                  },
                  {
                    text: 'Delete',
                    icon: (
                      <svg
                        className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500 group-focus:text-gray-500"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ),
                    onClick: onClickDelete,
                  },
                ]}
              />
              <Link to="?open=edit" className="hidden sm:block">
                <Button text="Edit" color={ButtonColors.plainWithBorder} />
              </Link>
            </div>
          }
        />
        <div className="px-4 py-5 sm:px-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
            <InfoItem
              label="Primary language"
              value={clinic.language === 'es' ? 'Spanish' : 'English'}
            />
            <InfoItem label="Plan" value={clinicPlan} />
            <InfoItem label="Renewal date" value={dayjs(clinic.billDate).format('MM/DD/YYYY')} />
            <InfoItem
              label="Subscription start date"
              value={dayjs(clinic.subscriptionStartDate).format('MM/DD/YYYY')}
            />
            <InfoItem label="Posting Schedule" value={clinic.postingSchedule} />
          </dl>
        </div>
      </SectionContainer>
      {/* <Button text="Subscribe" onClick={openBilling} /> */}
      {hasTeardownAccess && (
        <SectionContainer className="sm:shadow border border-gray-300 dark:border-dark-gray-800 mx-6 mt-6">
          <SectionHeader
            title="Messaging"
            subtitle="Manage messaging configuration for this clinic."
          />
          <div className="px-4 py-5 sm:px-6">
            <Button
              text="Teardown Messaging"
              color={ButtonColors.red}
              onClick={() => setTeardownModalOpen(true)}
            />
          </div>
        </SectionContainer>
      )}
      <Modal
        isOpen={teardownModalOpen}
        close={() => setTeardownModalOpen(false)}
        header={{ title: 'Teardown Messaging' }}
        footer={{
          buttons: [
            {
              text: 'Cancel',
              onClick: () => setTeardownModalOpen(false),
              color: ButtonColors.plainWithBorder,
            },
            {
              text: 'Teardown',
              onClick: onTeardownMessaging,
              loading: isTearingDown,
              color: ButtonColors.red,
            },
          ],
        }}
      >
        <div>
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <svg
              className="h-6 w-6 text-red-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div className="mt-3 text-center sm:mt-5">
            <h3
              className="text-lg leading-6 font-medium text-gray-900 dark:text-dark-gray-100"
              id="modal-headline"
            >
              Teardown Messaging
            </h3>
            <div className="mt-2">
              <p className="text-sm leading-5 text-gray-500 dark:text-dark-gray-400">
                Are you sure you want to teardown messaging for this clinic? This action cannot be
                undone.
              </p>
            </div>
          </div>
        </div>
      </Modal>
      <ClinicGeneralEditPanel
        editSlideOverState={editSlideOverState}
        updateSlideOverState={updateSlideOverState}
        defaultValues={clinic}
        update={update}
        isUpdating={isUpdating}
        user={user}
      />
      {clinic.ID && (
        <ClinicDeleteModal state={deleteModalState} setState={setDeleteModalState} del={del} />
      )}
    </>
  );
};

export default ClinicGeneral;
